import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiGlobe, FiCode, FiLink, FiSettings, FiInfo, FiCopy, FiExternalLink } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const IntegrationContainer = styled.div`
  margin: 0 auto;
  max-width: clamp(56.25rem, 75vw, 87.5rem);
  box-sizing: border-box;

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    max-width: clamp(80rem, 85vw, 87.5rem);
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    max-width: clamp(75rem, 80vw, 81.25rem);
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    max-width: clamp(68.75rem, 75vw, 75rem);
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    max-width: clamp(62.5rem, 70vw, 68.75rem);
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    max-width: clamp(56.25rem, 65vw, 62.5rem);
  }

  @media (max-width: 1023px) {
    max-width: clamp(50rem, 60vw, 56.25rem);
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 clamp(1rem, 2vw, 1.25rem);
  }
`;

const Header = styled.div`
  margin-bottom: clamp(1.25rem, 2vw, 2.5rem);
  box-sizing: border-box;

  h1 {
    color: #1a202c;
    font-weight: 700;
    display: flex;
    align-items: center;
    font-size: clamp(1.375rem, 2vw, 2rem);
    margin-bottom: clamp(0.5rem, 0.75vw, 0.75rem);
    gap: clamp(0.5rem, 1vw, 1rem);
  }
  
  p {
    color: #64748b;
    line-height: 1.6;
    font-size: clamp(0.875rem, 1.125vw, 1rem);
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    margin-bottom: clamp(2rem, 2.5vw, 2.5rem);

    h1 {
      font-size: clamp(1.75rem, 2.25vw, 2rem);
      margin-bottom: clamp(0.625rem, 0.875vw, 0.75rem);
      gap: clamp(0.75rem, 1.25vw, 1rem);
    }

    p {
      font-size: clamp(1rem, 1.25vw, 1rem);
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    margin-bottom: clamp(1.875rem, 2.25vw, 2.25rem);

    h1 {
      font-size: clamp(1.625rem, 2vw, 1.875rem);
      margin-bottom: clamp(0.5625rem, 0.75vw, 0.6875rem);
      gap: clamp(0.6875rem, 1.125vw, 0.9375rem);
    }

    p {
      font-size: clamp(0.9375rem, 1.125vw, 0.9375rem);
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    margin-bottom: clamp(1.75rem, 2vw, 2rem);

    h1 {
      font-size: clamp(1.5rem, 1.875vw, 1.75rem);
      margin-bottom: clamp(0.5rem, 0.625vw, 0.625rem);
      gap: clamp(0.625rem, 1vw, 0.875rem);
    }

    p {
      font-size: clamp(0.9375rem, 1.125vw, 0.9375rem);
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    margin-bottom: clamp(1.625rem, 1.875vw, 1.75rem);

    h1 {
      font-size: clamp(1.375rem, 1.75vw, 1.625rem);
      margin-bottom: clamp(0.4375rem, 0.5625vw, 0.5625rem);
      gap: clamp(0.5625rem, 0.875vw, 0.75rem);
    }

    p {
      font-size: clamp(0.875rem, 1vw, 0.875rem);
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    margin-bottom: clamp(1.5rem, 1.75vw, 1.5rem);

    h1 {
      font-size: clamp(1.25rem, 1.5vw, 1.5rem);
      margin-bottom: clamp(0.375rem, 0.5vw, 0.5rem);
      gap: clamp(0.5rem, 0.75vw, 0.625rem);
    }

    p {
      font-size: clamp(0.875rem, 1vw, 0.875rem);
    }
  }

  @media (max-width: 1023px) {
    margin-bottom: clamp(1.25rem, 1.5vw, 1.25rem);

    h1 {
      font-size: clamp(1.125rem, 1.375vw, 1.375rem);
      margin-bottom: clamp(0.3125rem, 0.4375vw, 0.5rem);
      gap: clamp(0.4375rem, 0.625vw, 0.5rem);
    }

    p {
      font-size: clamp(0.8125rem, 0.9375vw, 0.8125rem);
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: clamp(1rem, 1.25vw, 1.25rem);

    h1 {
      font-size: clamp(1rem, 1.25vw, 1.375rem);
      margin-bottom: clamp(0.25rem, 0.375vw, 0.5rem);
      gap: clamp(0.375rem, 0.5vw, 0.5rem);
    }

    p {
      font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
    }
  }
`;

const TabsContainer = styled.div`
  display: flex;
  background: #f8fafc;
  margin-bottom: clamp(1.25rem, 2vw, 2rem);
  border-radius: clamp(0.5rem, 0.75vw, 0.75rem);
  padding: clamp(0.1875rem, 0.25vw, 0.25rem);
  box-sizing: border-box;

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    margin-bottom: clamp(2rem, 2.5vw, 2.5rem);
    border-radius: clamp(0.75rem, 1vw, 0.875rem);
    padding: clamp(0.25rem, 0.375vw, 0.375rem);
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    margin-bottom: clamp(1.875rem, 2.25vw, 2.25rem);
    border-radius: clamp(0.6875rem, 0.875vw, 0.8125rem);
    padding: clamp(0.25rem, 0.375vw, 0.375rem);
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    margin-bottom: clamp(1.75rem, 2vw, 2rem);
    border-radius: clamp(0.625rem, 0.75vw, 0.75rem);
    padding: clamp(0.1875rem, 0.25vw, 0.25rem);
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    margin-bottom: clamp(1.5rem, 1.875vw, 1.75rem);
    border-radius: clamp(0.5625rem, 0.6875vw, 0.6875rem);
    padding: clamp(0.1875rem, 0.25vw, 0.25rem);
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    margin-bottom: clamp(1.375rem, 1.75vw, 1.5rem);
    border-radius: clamp(0.5rem, 0.625vw, 0.625rem);
    padding: clamp(0.1875rem, 0.25vw, 0.25rem);
  }

  @media (max-width: 1023px) {
    margin-bottom: clamp(1.25rem, 1.5vw, 1.25rem);
    border-radius: clamp(0.4375rem, 0.5625vw, 0.5625rem);
    padding: clamp(0.125rem, 0.1875vw, 0.1875rem);
  }
  
  @media (max-width: 768px) {
    margin-bottom: clamp(1rem, 1.25vw, 1.25rem);
    border-radius: clamp(0.4375rem, 0.5625vw, 0.5625rem);
    padding: clamp(0.125rem, 0.1875vw, 0.1875rem);
    flex-direction: column;
    gap: clamp(0.125rem, 0.1875vw, 0.1875rem);
  }
`;

const Tab = styled.button`
  flex: 1;
  background: ${props => props.active ? '#ffffff' : 'transparent'};
  color: ${props => props.active ? '#667eea' : '#64748b'};
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.active ? '0 2px 8px rgba(102, 126, 234, 0.15)' : 'none'};
  padding: clamp(0.5rem, 1vw, 1rem) clamp(0.75rem, 1.5vw, 1.25rem);
  border-radius: clamp(0.375rem, 0.5vw, 0.5rem);
  gap: clamp(0.375rem, 0.5vw, 0.5rem);
  font-size: clamp(0.75rem, 1vw, 0.9375rem);
  box-sizing: border-box;
  
  &:hover {
    color: #667eea;
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: clamp(0.75rem, 1.25vw, 1.25rem) clamp(1.25rem, 2vw, 1.5rem);
    border-radius: clamp(0.5rem, 0.75vw, 0.75rem);
    gap: clamp(0.5rem, 0.75vw, 0.75rem);
    font-size: clamp(0.9375rem, 1.125vw, 1rem);
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: clamp(0.6875rem, 1.125vw, 1.125rem) clamp(1.125rem, 1.875vw, 1.375rem);
    border-radius: clamp(0.5rem, 0.75vw, 0.75rem);
    gap: clamp(0.5rem, 0.75vw, 0.75rem);
    font-size: clamp(0.875rem, 1vw, 0.9375rem);
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: clamp(0.625rem, 1vw, 1rem) clamp(1rem, 1.75vw, 1.25rem);
    border-radius: clamp(0.4375rem, 0.625vw, 0.625rem);
    gap: clamp(0.4375rem, 0.625vw, 0.625rem);
    font-size: clamp(0.875rem, 1vw, 0.9375rem);
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: clamp(0.5625rem, 0.875vw, 0.9375rem) clamp(0.875rem, 1.5vw, 1.125rem);
    border-radius: clamp(0.375rem, 0.5vw, 0.5rem);
    gap: clamp(0.375rem, 0.5vw, 0.5rem);
    font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: clamp(0.5rem, 0.75vw, 0.875rem) clamp(0.75rem, 1.25vw, 1rem);
    border-radius: clamp(0.375rem, 0.5vw, 0.5rem);
    gap: clamp(0.375rem, 0.5vw, 0.5rem);
    font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
  }

  @media (max-width: 1023px) {
    padding: clamp(0.4375rem, 0.625vw, 0.75rem) clamp(0.625rem, 1vw, 0.875rem);
    border-radius: clamp(0.3125rem, 0.4375vw, 0.4375rem);
    gap: clamp(0.3125rem, 0.4375vw, 0.4375rem);
    font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
  }
  
  @media (max-width: 768px) {
    padding: clamp(0.375rem, 0.5vw, 0.625rem) clamp(0.5rem, 0.875vw, 0.75rem);
    border-radius: clamp(0.3125rem, 0.4375vw, 0.4375rem);
    gap: clamp(0.3125rem, 0.4375vw, 0.4375rem);
    font-size: clamp(0.6875rem, 0.8125vw, 0.75rem);
  }
`;

const ContentCard = styled.div`
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  border-radius: clamp(0.75rem, 1vw, 1rem);
  padding: clamp(1.25rem, 2vw, 2rem);
  box-sizing: border-box;

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    border-radius: clamp(1rem, 1.25vw, 1.25rem);
    padding: clamp(2rem, 2.5vw, 2.5rem);
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    border-radius: clamp(0.9375rem, 1.125vw, 1.125rem);
    padding: clamp(1.875rem, 2.25vw, 2.25rem);
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    border-radius: clamp(0.875rem, 1vw, 1rem);
    padding: clamp(1.75rem, 2vw, 2rem);
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    border-radius: clamp(0.8125rem, 0.9375vw, 0.9375rem);
    padding: clamp(1.5rem, 1.875vw, 1.75rem);
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    border-radius: clamp(0.75rem, 0.875vw, 0.875rem);
    padding: clamp(1.375rem, 1.75vw, 1.5rem);
  }

  @media (max-width: 1023px) {
    border-radius: clamp(0.6875rem, 0.8125vw, 0.8125rem);
    padding: clamp(1.25rem, 1.5vw, 1.25rem);
  }
  
  @media (max-width: 768px) {
    border-radius: clamp(0.625rem, 0.75vw, 0.75rem);
    padding: clamp(1rem, 1.25vw, 1.25rem);
  }
`;

const Section = styled.div`
  &:last-child {
    margin-bottom: 0;
  }
  margin-bottom: clamp(1.25rem, 2vw, 2rem);
  box-sizing: border-box;

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    margin-bottom: clamp(2rem, 2.5vw, 2.5rem);
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    margin-bottom: clamp(1.875rem, 2.25vw, 2.25rem);
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    margin-bottom: clamp(1.75rem, 2vw, 2rem);
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    margin-bottom: clamp(1.5rem, 1.875vw, 1.75rem);
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    margin-bottom: clamp(1.375rem, 1.75vw, 1.5rem);
  }

  @media (max-width: 1023px) {
    margin-bottom: clamp(1.25rem, 1.5vw, 1.25rem);
  }
  
  @media (max-width: 768px) {
    margin-bottom: clamp(1rem, 1.25vw, 1.25rem);
  }
`;

const SectionTitle = styled.h2`
  color: #1a202c;
  font-weight: 600;
  display: flex;
  align-items: center;
  font-size: clamp(1.125rem, 1.5vw, 1.5rem);
  margin-bottom: clamp(0.75rem, 1vw, 1rem);
  gap: clamp(0.5rem, 0.75vw, 0.75rem);
  box-sizing: border-box;

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    font-size: clamp(1.5rem, 1.875vw, 1.75rem);
    margin-bottom: clamp(1rem, 1.25vw, 1.25rem);
    gap: clamp(0.75rem, 1vw, 0.875rem);
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    font-size: clamp(1.375rem, 1.75vw, 1.625rem);
    margin-bottom: clamp(0.9375rem, 1.125vw, 1.125rem);
    gap: clamp(0.6875rem, 0.875vw, 0.8125rem);
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    font-size: clamp(1.25rem, 1.5vw, 1.5rem);
    margin-bottom: clamp(0.875rem, 1vw, 1rem);
    gap: clamp(0.625rem, 0.75vw, 0.75rem);
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    font-size: clamp(1.125rem, 1.375vw, 1.375rem);
    margin-bottom: clamp(0.8125rem, 0.9375vw, 0.9375rem);
    gap: clamp(0.5625rem, 0.6875vw, 0.6875rem);
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    font-size: clamp(1rem, 1.25vw, 1.25rem);
    margin-bottom: clamp(0.75rem, 0.875vw, 0.875rem);
    gap: clamp(0.5rem, 0.625vw, 0.625rem);
  }

  @media (max-width: 1023px) {
    font-size: clamp(0.9375rem, 1.125vw, 1.125rem);
    margin-bottom: clamp(0.6875rem, 0.8125vw, 0.8125rem);
    gap: clamp(0.4375rem, 0.5625vw, 0.5625rem);
  }
  
  @media (max-width: 768px) {
    font-size: clamp(0.875rem, 1vw, 1.125rem);
    margin-bottom: clamp(0.625rem, 0.75vw, 0.75rem);
    gap: clamp(0.375rem, 0.5vw, 0.5rem);
  }
`;

const SubTitle = styled.h3`
  color: #374151;
  font-weight: 600;
  font-size: clamp(0.875rem, 1.125vw, 1.125rem);
  margin-bottom: clamp(0.5rem, 0.75vw, 0.75rem);
  margin-top: clamp(1rem, 1.5vw, 1.5rem);
  box-sizing: border-box;

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    font-size: clamp(1.125rem, 1.375vw, 1.375rem);
    margin-bottom: clamp(0.75rem, 1vw, 0.875rem);
    margin-top: clamp(1.5rem, 1.875vw, 1.875rem);
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    font-size: clamp(1.0625rem, 1.25vw, 1.25rem);
    margin-bottom: clamp(0.6875rem, 0.875vw, 0.8125rem);
    margin-top: clamp(1.375rem, 1.75vw, 1.625rem);
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    font-size: clamp(1rem, 1.125vw, 1.125rem);
    margin-bottom: clamp(0.625rem, 0.75vw, 0.75rem);
    margin-top: clamp(1.25rem, 1.5vw, 1.375rem);
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    font-size: clamp(0.9375rem, 1.0625vw, 1rem);
    margin-bottom: clamp(0.5625rem, 0.6875vw, 0.6875rem);
    margin-top: clamp(1.125rem, 1.375vw, 1.25rem);
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    font-size: clamp(0.9375rem, 1.0625vw, 1rem);
    margin-bottom: clamp(0.5rem, 0.625vw, 0.625rem);
    margin-top: clamp(1rem, 1.25vw, 1.125rem);
  }

  @media (max-width: 1023px) {
    font-size: clamp(0.875rem, 1vw, 0.9375rem);
    margin-bottom: clamp(0.5rem, 0.625vw, 0.625rem);
    margin-top: clamp(1rem, 1.125vw, 1rem);
  }
  
  @media (max-width: 768px) {
    font-size: clamp(0.875rem, 1vw, 0.9375rem);
    margin-bottom: clamp(0.5rem, 0.625vw, 0.625rem);
    margin-top: clamp(1rem, 1.125vw, 1rem);
  }
`;

const StepList = styled.ol`
  list-style: none;
  padding: 0;
  counter-reset: step-counter;
`;

const Step = styled.li`
  counter-increment: step-counter;
  position: relative;
  margin-bottom: clamp(0.75rem, 1.25vw, 1.25rem);
  padding-left: clamp(2rem, 2.5vw, 2.5rem);
  box-sizing: border-box;
  
  &::before {
    content: counter(step-counter);
    position: absolute;
    left: 0;
    top: 0;
    background: #667eea;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    width: clamp(1.25rem, 1.75vw, 1.75rem);
    height: clamp(1.25rem, 1.75vw, 1.75rem);
    font-size: clamp(0.625rem, 0.875vw, 0.875rem);
  }
  
  strong {
    color: #1a202c;
    font-weight: 600;
    font-size: clamp(0.8125rem, 1vw, 0.9375rem);
  }
  
  p {
    color: #64748b;
    line-height: 1.6;
    font-size: clamp(0.75rem, 0.9375vw, 0.875rem);
    margin-top: clamp(0.25rem, 0.5vw, 0.5rem);
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    margin-bottom: clamp(1.25rem, 1.5vw, 1.5rem);
    padding-left: clamp(2.5rem, 3vw, 3rem);

    &::before {
      width: clamp(1.75rem, 2vw, 2rem);
      height: clamp(1.75rem, 2vw, 2rem);
      font-size: clamp(0.875rem, 1vw, 1rem);
    }

    strong {
      font-size: clamp(0.9375rem, 1.125vw, 1.0625rem);
    }

    p {
      font-size: clamp(0.875rem, 1vw, 0.9375rem);
      margin-top: clamp(0.5rem, 0.625vw, 0.625rem);
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    margin-bottom: clamp(1.125rem, 1.375vw, 1.375rem);
    padding-left: clamp(2.25rem, 2.75vw, 2.75rem);

    &::before {
      width: clamp(1.5rem, 1.875vw, 1.875rem);
      height: clamp(1.5rem, 1.875vw, 1.875rem);
      font-size: clamp(0.8125rem, 0.9375vw, 0.9375rem);
    }

    strong {
      font-size: clamp(0.875rem, 1vw, 0.9375rem);
    }

    p {
      font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
      margin-top: clamp(0.4375rem, 0.5625vw, 0.5625rem);
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    margin-bottom: clamp(1rem, 1.25vw, 1.25rem);
    padding-left: clamp(2.25rem, 2.5vw, 2.5rem);

    &::before {
      width: clamp(1.5rem, 1.75vw, 1.75rem);
      height: clamp(1.5rem, 1.75vw, 1.75rem);
      font-size: clamp(0.75rem, 0.875vw, 0.875rem);
    }

    strong {
      font-size: clamp(0.875rem, 1vw, 0.9375rem);
    }

    p {
      font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
      margin-top: clamp(0.375rem, 0.5vw, 0.5rem);
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    margin-bottom: clamp(0.875rem, 1.125vw, 1.125rem);
    padding-left: clamp(2.125rem, 2.375vw, 2.375rem);

    &::before {
      width: clamp(1.375rem, 1.625vw, 1.625rem);
      height: clamp(1.375rem, 1.625vw, 1.625rem);
      font-size: clamp(0.6875rem, 0.8125vw, 0.8125rem);
    }

    strong {
      font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
    }

    p {
      font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
      margin-top: clamp(0.3125rem, 0.4375vw, 0.4375rem);
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    margin-bottom: clamp(0.75rem, 1vw, 1rem);
    padding-left: clamp(2rem, 2.25vw, 2.25rem);

    &::before {
      width: clamp(1.25rem, 1.5vw, 1.5rem);
      height: clamp(1.25rem, 1.5vw, 1.5rem);
      font-size: clamp(0.6875rem, 0.8125vw, 0.8125rem);
    }

    strong {
      font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
    }

    p {
      font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
      margin-top: clamp(0.3125rem, 0.4375vw, 0.4375rem);
    }
  }

  @media (max-width: 1023px) {
    margin-bottom: clamp(0.75rem, 1vw, 1rem);
    padding-left: clamp(2rem, 2.25vw, 2.25rem);

    &::before {
      width: clamp(1.25rem, 1.5vw, 1.5rem);
      height: clamp(1.25rem, 1.5vw, 1.5rem);
      font-size: clamp(0.6875rem, 0.8125vw, 0.8125rem);
    }

    strong {
      font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
    }

    p {
      font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
      margin-top: clamp(0.3125rem, 0.4375vw, 0.4375rem);
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: clamp(0.75rem, 1vw, 1rem);
    padding-left: clamp(1.875rem, 2.125vw, 2.125rem);

    &::before {
      width: clamp(1.125rem, 1.375vw, 1.375rem);
      height: clamp(1.125rem, 1.375vw, 1.375rem);
      font-size: clamp(0.625rem, 0.75vw, 0.75rem);
    }

    strong {
      font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
    }

    p {
      font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
      margin-top: clamp(0.25rem, 0.375vw, 0.375rem);
    }
  }
`;

const CodeBlock = styled.div`
  background: #1e293b;
  color: #e2e8f0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
  position: relative;
  overflow-x: auto;
  padding: clamp(1rem, 1.5vw, 1.25rem);
  border-radius: clamp(0.5rem, 0.75vw, 0.75rem);
  font-size: clamp(0.75rem, 0.9375vw, 0.875rem);
  margin: clamp(0.75rem, 1vw, 1rem) 0;
  box-sizing: border-box;
  
  .copy-btn {
    position: absolute;
    background: #374151;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    display: flex;
    align-items: center;
    top: clamp(0.5rem, 0.75vw, 0.75rem);
    right: clamp(0.5rem, 0.75vw, 0.75rem);
    padding: clamp(0.25rem, 0.5vw, 0.5rem);
    border-radius: clamp(0.25rem, 0.375vw, 0.375rem);
    gap: clamp(0.125rem, 0.25vw, 0.25rem);
    font-size: clamp(0.625rem, 0.75vw, 0.75rem);
    
    &:hover {
      background: #4b5563;
      color: #ffffff;
    }
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    padding: clamp(1.25rem, 1.75vw, 1.75rem);
    border-radius: clamp(0.75rem, 1vw, 1rem);
    font-size: clamp(0.875rem, 1vw, 0.9375rem);
    margin: clamp(1rem, 1.25vw, 1.25rem) 0;

    .copy-btn {
      top: clamp(0.75rem, 1vw, 1rem);
      right: clamp(0.75rem, 1vw, 1rem);
      padding: clamp(0.5rem, 0.75vw, 0.75rem);
      border-radius: clamp(0.375rem, 0.5vw, 0.5rem);
      gap: clamp(0.25rem, 0.375vw, 0.375rem);
      font-size: clamp(0.75rem, 0.875vw, 0.875rem);
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    padding: clamp(1.125rem, 1.5vw, 1.5rem);
    border-radius: clamp(0.6875rem, 0.875vw, 0.875rem);
    font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
    margin: clamp(0.875rem, 1.125vw, 1.125rem) 0;

    .copy-btn {
      top: clamp(0.6875rem, 0.875vw, 0.875rem);
      right: clamp(0.6875rem, 0.875vw, 0.875rem);
      padding: clamp(0.4375rem, 0.625vw, 0.625rem);
      border-radius: clamp(0.3125rem, 0.4375vw, 0.4375rem);
      gap: clamp(0.1875rem, 0.3125vw, 0.3125rem);
      font-size: clamp(0.6875rem, 0.8125vw, 0.8125rem);
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    padding: clamp(1rem, 1.25vw, 1.25rem);
    border-radius: clamp(0.625rem, 0.75vw, 0.75rem);
    font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
    margin: clamp(0.875rem, 1vw, 1rem) 0;

    .copy-btn {
      top: clamp(0.625rem, 0.75vw, 0.75rem);
      right: clamp(0.625rem, 0.75vw, 0.75rem);
      padding: clamp(0.375rem, 0.5vw, 0.5rem);
      border-radius: clamp(0.25rem, 0.375vw, 0.375rem);
      gap: clamp(0.1875rem, 0.25vw, 0.25rem);
      font-size: clamp(0.6875rem, 0.8125vw, 0.8125rem);
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    padding: clamp(0.875rem, 1.125vw, 1.125rem);
    border-radius: clamp(0.5625rem, 0.6875vw, 0.6875rem);
    font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
    margin: clamp(0.8125rem, 0.9375vw, 0.9375rem) 0;

    .copy-btn {
      top: clamp(0.5625rem, 0.6875vw, 0.6875rem);
      right: clamp(0.5625rem, 0.6875vw, 0.6875rem);
      padding: clamp(0.3125rem, 0.4375vw, 0.4375rem);
      border-radius: clamp(0.25rem, 0.3125vw, 0.3125rem);
      gap: clamp(0.1875rem, 0.25vw, 0.25rem);
      font-size: clamp(0.6875rem, 0.8125vw, 0.8125rem);
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    padding: clamp(0.75rem, 1vw, 1rem);
    border-radius: clamp(0.5rem, 0.625vw, 0.625rem);
    font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
    margin: clamp(0.75rem, 0.875vw, 0.875rem) 0;

    .copy-btn {
      top: clamp(0.5rem, 0.625vw, 0.625rem);
      right: clamp(0.5rem, 0.625vw, 0.625rem);
      padding: clamp(0.3125rem, 0.4375vw, 0.4375rem);
      border-radius: clamp(0.25rem, 0.3125vw, 0.3125rem);
      gap: clamp(0.125rem, 0.1875vw, 0.1875rem);
      font-size: clamp(0.625rem, 0.75vw, 0.75rem);
    }
  }

  @media (max-width: 1023px) {
    padding: clamp(0.75rem, 1vw, 1rem);
    border-radius: clamp(0.5rem, 0.625vw, 0.625rem);
    font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
    margin: clamp(0.75rem, 0.875vw, 0.875rem) 0;

    .copy-btn {
      top: clamp(0.5rem, 0.625vw, 0.625rem);
      right: clamp(0.5rem, 0.625vw, 0.625rem);
      padding: clamp(0.3125rem, 0.4375vw, 0.4375rem);
      border-radius: clamp(0.25rem, 0.3125vw, 0.3125rem);
      gap: clamp(0.125rem, 0.1875vw, 0.1875rem);
      font-size: clamp(0.625rem, 0.75vw, 0.75rem);
    }
  }
  
  @media (max-width: 768px) {
    padding: clamp(0.625rem, 0.875vw, 0.875rem);
    border-radius: clamp(0.4375rem, 0.5625vw, 0.5625rem);
    font-size: clamp(0.6875rem, 0.8125vw, 0.75rem);
    margin: clamp(0.625rem, 0.75vw, 0.75rem) 0;

    .copy-btn {
      top: clamp(0.4375rem, 0.5625vw, 0.5625rem);
      right: clamp(0.4375rem, 0.5625vw, 0.5625rem);
      padding: clamp(0.25rem, 0.375vw, 0.375rem);
      border-radius: clamp(0.1875rem, 0.25vw, 0.25rem);
      gap: clamp(0.125rem, 0.1875vw, 0.1875rem);
      font-size: clamp(0.625rem, 0.75vw, 0.75rem);
    }
  }
`;

const InfoBox = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  display: flex;
  border-radius: clamp(0.5rem, 0.75vw, 0.75rem);
  padding: clamp(1rem, 1.5vw, 1.25rem);
  margin: clamp(1rem, 1.5vw, 1.25rem) 0;
  gap: clamp(0.5rem, 0.75vw, 0.75rem);
  box-sizing: border-box;
  
  .icon {
    color: #0284c7;
    flex-shrink: 0;
    font-size: clamp(1rem, 1.25vw, 1.25rem);
    margin-top: clamp(0.125rem, 0.25vw, 0.25rem);
  }
  
  .content {
    h4 {
      color: #0c4a6e;
      font-weight: 600;
      font-size: clamp(0.875rem, 1vw, 1rem);
      margin-bottom: clamp(0.375rem, 0.5vw, 0.5rem);
    }
    
    p {
      color: #075985;
      line-height: 1.6;
      margin: 0;
      font-size: clamp(0.75rem, 0.875vw, 0.875rem);
    }
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    border-radius: clamp(0.75rem, 1vw, 1rem);
    padding: clamp(1.25rem, 1.75vw, 1.75rem);
    margin: clamp(1.25rem, 1.75vw, 1.75rem) 0;
    gap: clamp(0.75rem, 1vw, 1rem);

    .icon {
      font-size: clamp(1.25rem, 1.5vw, 1.5rem);
      margin-top: clamp(0.125rem, 0.25vw, 0.25rem);
    }

    .content {
      h4 {
        font-size: clamp(1rem, 1.25vw, 1.125rem);
        margin-bottom: clamp(0.5rem, 0.625vw, 0.625rem);
      }
      p {
        font-size: clamp(0.875rem, 1vw, 0.9375rem);
      }
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    border-radius: clamp(0.6875rem, 0.875vw, 0.875rem);
    padding: clamp(1.125rem, 1.5vw, 1.5rem);
    margin: clamp(1.125rem, 1.5vw, 1.5rem) 0;
    gap: clamp(0.6875rem, 0.875vw, 0.875rem);

    .icon {
      font-size: clamp(1.125rem, 1.375vw, 1.375rem);
      margin-top: clamp(0.125rem, 0.25vw, 0.25rem);
    }

    .content {
      h4 {
        font-size: clamp(0.9375rem, 1.125vw, 1.0625rem);
        margin-bottom: clamp(0.4375rem, 0.5625vw, 0.5625rem);
      }
      p {
        font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
      }
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    border-radius: clamp(0.625rem, 0.75vw, 0.75rem);
    padding: clamp(1rem, 1.25vw, 1.25rem);
    margin: clamp(1rem, 1.25vw, 1.25rem) 0;
    gap: clamp(0.625rem, 0.75vw, 0.75rem);

    .icon {
      font-size: clamp(1.125rem, 1.25vw, 1.25rem);
      margin-top: clamp(0.0625rem, 0.125vw, 0.125rem);
    }

    .content {
      h4 {
        font-size: clamp(0.875rem, 1vw, 0.9375rem);
        margin-bottom: clamp(0.375rem, 0.5vw, 0.5rem);
      }
      p {
        font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
      }
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    border-radius: clamp(0.5625rem, 0.6875vw, 0.6875rem);
    padding: clamp(0.875rem, 1.125vw, 1.125rem);
    margin: clamp(0.875rem, 1.125vw, 1.125rem) 0;
    gap: clamp(0.5625rem, 0.6875vw, 0.6875rem);

    .icon {
      font-size: clamp(1.0625rem, 1.125vw, 1.125rem);
      margin-top: clamp(0.0625rem, 0.125vw, 0.125rem);
    }

    .content {
      h4 {
        font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
        margin-bottom: clamp(0.3125rem, 0.4375vw, 0.4375rem);
      }
      p {
        font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
      }
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    border-radius: clamp(0.5rem, 0.625vw, 0.625rem);
    padding: clamp(0.75rem, 1vw, 1rem);
    margin: clamp(0.75rem, 1vw, 1rem) 0;
    gap: clamp(0.5rem, 0.625vw, 0.625rem);

    .icon {
      font-size: clamp(1rem, 1.125vw, 1.125rem);
      margin-top: clamp(0.0625rem, 0.125vw, 0.125rem);
    }

    .content {
      h4 {
        font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
        margin-bottom: clamp(0.25rem, 0.375vw, 0.375rem);
      }
      p {
        font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
      }
    }
  }

  @media (max-width: 1023px) {
    border-radius: clamp(0.5rem, 0.625vw, 0.625rem);
    padding: clamp(0.75rem, 1vw, 1rem);
    margin: clamp(0.75rem, 1vw, 1rem) 0;
    gap: clamp(0.5rem, 0.625vw, 0.625rem);

    .icon {
      font-size: clamp(1rem, 1.125vw, 1.125rem);
      margin-top: clamp(0.0625rem, 0.125vw, 0.125rem);
    }

    .content {
      h4 {
        font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
        margin-bottom: clamp(0.25rem, 0.375vw, 0.375rem);
      }
      p {
        font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
      }
    }
  }
  
  @media (max-width: 768px) {
    border-radius: clamp(0.4375rem, 0.5625vw, 0.5625rem);
    padding: clamp(0.625rem, 0.875vw, 0.875rem);
    margin: clamp(0.625rem, 0.875vw, 0.875rem) 0;
    gap: clamp(0.5rem, 0.625vw, 0.625rem);

    .icon {
      font-size: clamp(0.9375rem, 1vw, 1rem);
      margin-top: clamp(0.0625rem, 0.125vw, 0.125rem);
    }

    .content {
      h4 {
        font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
        margin-bottom: clamp(0.25rem, 0.375vw, 0.375rem);
      }
      p {
        font-size: clamp(0.6875rem, 0.8125vw, 0.75rem);
      }
    }
  }
`;

const ConfigTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: clamp(1rem, 1.5vw, 1.25rem) 0;
  background: #f8fafc;
  border-radius: clamp(0.5rem, 0.75vw, 0.75rem);
  overflow: hidden;
  box-sizing: border-box;
  
  th, td {
    padding: clamp(0.75rem, 1vw, 1rem) clamp(1rem, 1.5vw, 1.25rem);
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }
  
  th {
    background: #667eea;
    color: white;
    font-weight: 600;
  }
  
  td {
    color: #374151;
    
    code {
      background: #e2e8f0;
      padding: clamp(0.125rem, 0.25vw, 0.25rem) clamp(0.375rem, 0.5vw, 0.5rem);
      border-radius: clamp(0.25rem, 0.375vw, 0.375rem);
      font-family: monospace;
      font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
    }
  }

  /* Responsive sizing for different desktop resolutions */
  @media (min-width: 1920px) {
    margin: clamp(1.25rem, 1.75vw, 1.75rem) 0;
    border-radius: clamp(0.75rem, 1vw, 1rem);

    th, td {
      padding: clamp(1rem, 1.25vw, 1.25rem) clamp(1.25rem, 1.75vw, 1.75rem);
    }

    td code {
      padding: clamp(0.25rem, 0.375vw, 0.375rem) clamp(0.5rem, 0.75vw, 0.75rem);
      border-radius: clamp(0.375rem, 0.5vw, 0.5rem);
      font-size: clamp(0.875rem, 1vw, 0.9375rem);
    }
  }

  @media (min-width: 1440px) and (max-width: 1919px) {
    margin: clamp(1.125rem, 1.5vw, 1.5rem) 0;
    border-radius: clamp(0.6875rem, 0.875vw, 0.875rem);

    th, td {
      padding: clamp(0.9375rem, 1.125vw, 1.125rem) clamp(1.125rem, 1.5vw, 1.5rem);
    }

    td code {
      padding: clamp(0.1875rem, 0.3125vw, 0.3125rem) clamp(0.4375rem, 0.625vw, 0.625rem);
      border-radius: clamp(0.3125rem, 0.4375vw, 0.4375rem);
      font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
    }
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    margin: clamp(1rem, 1.25vw, 1.25rem) 0;
    border-radius: clamp(0.625rem, 0.75vw, 0.75rem);

    th, td {
      padding: clamp(0.875rem, 1vw, 1rem) clamp(1rem, 1.25vw, 1.25rem);
    }

    td code {
      padding: clamp(0.1875rem, 0.25vw, 0.25rem) clamp(0.375rem, 0.5vw, 0.5rem);
      border-radius: clamp(0.25rem, 0.375vw, 0.375rem);
      font-size: clamp(0.8125rem, 0.9375vw, 0.875rem);
    }
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    margin: clamp(0.875rem, 1.125vw, 1.125rem) 0;
    border-radius: clamp(0.5625rem, 0.6875vw, 0.6875rem);

    th, td {
      padding: clamp(0.8125rem, 0.9375vw, 0.9375rem) clamp(0.875rem, 1.125vw, 1.125rem);
    }

    td code {
      padding: clamp(0.125rem, 0.1875vw, 0.1875rem) clamp(0.375rem, 0.5vw, 0.5rem);
      border-radius: clamp(0.25rem, 0.3125vw, 0.3125rem);
      font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
    }
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    margin: clamp(0.75rem, 1vw, 1rem) 0;
    border-radius: clamp(0.5rem, 0.625vw, 0.625rem);

    th, td {
      padding: clamp(0.75rem, 0.875vw, 0.875rem) clamp(0.75rem, 1vw, 1rem);
    }

    td code {
      padding: clamp(0.125rem, 0.1875vw, 0.1875rem) clamp(0.375rem, 0.5vw, 0.5rem);
      border-radius: clamp(0.25rem, 0.3125vw, 0.3125rem);
      font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
    }
  }

  @media (max-width: 1023px) {
    margin: clamp(0.75rem, 1vw, 1rem) 0;
    border-radius: clamp(0.5rem, 0.625vw, 0.625rem);

    th, td {
      padding: clamp(0.75rem, 0.875vw, 0.875rem) clamp(0.75rem, 1vw, 1rem);
    }

    td code {
      padding: clamp(0.125rem, 0.1875vw, 0.1875rem) clamp(0.375rem, 0.5vw, 0.5rem);
      border-radius: clamp(0.25rem, 0.3125vw, 0.3125rem);
      font-size: clamp(0.75rem, 0.875vw, 0.8125rem);
    }
  }
  
  @media (max-width: 768px) {
    margin: clamp(0.625rem, 0.875vw, 0.875rem) 0;
    border-radius: clamp(0.4375rem, 0.5625vw, 0.5625rem);

    th, td {
      padding: clamp(0.625rem, 0.75vw, 0.75rem) clamp(0.625rem, 0.875vw, 0.875rem);
    }

    td code {
      padding: clamp(0.125rem, 0.1875vw, 0.1875rem) clamp(0.3125rem, 0.4375vw, 0.4375rem);
      border-radius: clamp(0.1875rem, 0.25vw, 0.25rem);
      font-size: clamp(0.6875rem, 0.8125vw, 0.75rem);
    }
  }
`;

const IntegrationPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('website');
  const navigate = useNavigate();
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
  };

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/dashboard');
    }
  }, [user]);

  const websiteIntegration = (
    <ContentCard>
      <Section>
        <SectionTitle>
          <FiGlobe />
          Website Integration
        </SectionTitle>
        <p>Integrate your website with our CRM to automatically capture and manage leads from contact forms, landing pages, and other web interactions.</p>

        <SubTitle>API Integration</SubTitle>
        <StepList>
          <Step>
            <strong>Get your API credentials</strong>
            <p>Use the secure API key "CURDATEYEAR" for external integrations.</p>
          </Step>
                  <Step>
          <strong>Set up the endpoint</strong>
          <p>Use the following endpoint to submit leads to our CRM:</p>
          <CodeBlock>
            <button className="copy-btn" onClick={() => copyToClipboard('POST https://api.pleasureindiaholidays.com/api/leads')}>
              <FiCopy /> Copy
            </button>
            POST https://api.pleasureindiaholidays.com/api/leads
          </CodeBlock>
        </Step>
          <Step>
            <strong>Configure authentication</strong>
            <p>Include your API key in the request headers:</p>
            <CodeBlock>
              <button className="copy-btn" onClick={() => copyToClipboard('{\n  "x-api-key": "CURDATEYEAR",\n  "Content-Type": "application/json"\n}')}>
                <FiCopy /> Copy
              </button>
              {`{
  "x-api-key": "CURDATEYEAR",
  "Content-Type": "application/json"
}`}
            </CodeBlock>
            <p>Alternatively, use the Authorization header:</p>
            <CodeBlock>
              <button className="copy-btn" onClick={() => copyToClipboard('{\n  "Authorization": "Bearer CURDATEYEAR",\n  "Content-Type": "application/json"\n}')}>
                <FiCopy /> Copy
              </button>
              {`{
  "Authorization": "Bearer CURDATEYEAR",
  "Content-Type": "application/json"
}`}
            </CodeBlock>
          </Step>
          <Step>
            <strong>Send lead data</strong>
            <p>Submit lead information using this JSON structure:</p>
            <CodeBlock>
              <button className="copy-btn" onClick={() => copyToClipboard('{\n  "name": "John Doe",\n  "email": "john@example.com",\n  "phone": "+1234567890",\n  "tour_name": "Bali Adventure Package",\n  "travel_date": "2024-06-15",\n  "number_of_passengers": 2,\n  "message": "Interested in honeymoon package",\n  "source": "website-contact-form"\n}')}>
                <FiCopy /> Copy
              </button>
              {`{
  "name": "John Doe",
  "email": "john@example.com",
  "phone": "+1234567890",
  "tour_name": "Bali Adventure Package",
  "travel_date": "2024-06-15",
  "number_of_passengers": 2,
  "message": "Closed in honeymoon package",
  "source": "website-contact-form"
}`}
            </CodeBlock>
          </Step>
        </StepList>

        <InfoBox>
          <FiInfo className="icon" />
          <div className="content">
            <h4>Required Fields</h4>
            <p><strong>Required:</strong> name, and either email OR phone. All other fields are optional but recommended for better lead qualification.</p>
          </div>
        </InfoBox>

        <SubTitle>HTML Form Integration</SubTitle>
        <p>For simple contact forms, you can use this HTML template:</p>
        <CodeBlock>
          <button className="copy-btn" onClick={() => copyToClipboard('<form id="leadForm" onsubmit="submitLead(event)">\n  <input name="name" placeholder="Full Name" required>\n  <input name="email" type="email" placeholder="Email" required>\n  <input name="phone" placeholder="Phone Number">\n  <select name="tour_name">\n    <option value="Bali Package">Bali Package</option>\n    <option value="Thailand Tour">Thailand Tour</option>\n  </select>\n  <input name="travel_date" type="date">\n  <input name="number_of_passengers" type="number" placeholder="Number of Travelers">\n  <textarea name="message" placeholder="Your Message"></textarea>\n  <button type="submit">Submit</button>\n</form>')}>
            <FiCopy /> Copy
          </button>
          {`<form id="leadForm" onsubmit="submitLead(event)">
  <input name="name" placeholder="Full Name" required>
  <input name="email" type="email" placeholder="Email" required>
  <input name="phone" placeholder="Phone Number">
  <select name="tour_name">
    <option value="Bali Package">Bali Package</option>
    <option value="Thailand Tour">Thailand Tour</option>
  </select>
  <input name="travel_date" type="date">
  <input name="number_of_passengers" type="number" placeholder="Number of Travelers">
  <textarea name="message" placeholder="Your Message"></textarea>
  <button type="submit">Submit</button>
</form>`}
        </CodeBlock>

        <SubTitle>JavaScript Implementation</SubTitle>
        <CodeBlock>
          <button className="copy-btn" onClick={() => copyToClipboard('async function submitLead(event) {\n  event.preventDefault();\n  const formData = new FormData(event.target);\n  const leadData = Object.fromEntries(formData.entries());\n  \n  try {\n    const response = await fetch("https://api.pleasureindiaholidays.com/api/leads", {\n      method: "POST",\n      headers: {\n        "x-api-key": "CURDATEYEAR",\n        "Content-Type": "application/json"\n      },\n      body: JSON.stringify(leadData)\n    });\n    \n    if (response.ok) {\n      alert("Thank you! We will contact you soon.");\n      event.target.reset();\n    } else {\n      alert("Something went wrong. Please try again.");\n    }\n  } catch (error) {\n    alert("Network error. Please check your connection.");\n  }\n}')}>
            <FiCopy /> Copy
          </button>
          {`async function submitLead(event) {
  event.preventDefault();
  const formData = new FormData(event.target);
  const leadData = Object.fromEntries(formData.entries());
  
  try {
    const response = await fetch("https://api.pleasureindiaholidays.com/api/leads", {
      method: "POST",
      headers: {
        "x-api-key": "CURDATEYEAR",
        "Content-Type": "application/json"
      },
      body: JSON.stringify(leadData)
    });
    
    if (response.ok) {
      alert("Thank you! We will contact you soon.");
      event.target.reset();
    } else {
      alert("Something went wrong. Please try again.");
    }
  } catch (error) {
    alert("Network error. Please check your connection.");
  }
}`}
        </CodeBlock>

        <SubTitle>Response Format</SubTitle>
        <p>The API returns standardized JSON responses:</p>
        
        <SubTitle>Success Response (201)</SubTitle>
        <CodeBlock>
          <button className="copy-btn" onClick={() => copyToClipboard('{\n  "success": true,\n  "message": "Lead created successfully",\n  "data": {\n    "id": "LD1234567890",\n    "name": "John Doe",\n    "email": "john@example.com",\n    "phone": "+1234567890",\n    "source": "website-contact-form",\n    "status": "new",\n    "created_at": "2024-01-15T10:30:00Z",\n    "assigned_to": null\n  }\n}')}>
            <FiCopy /> Copy
          </button>
          {`{
  "success": true,
  "message": "Lead created successfully",
  "data": {
    "id": "LD1234567890",
    "name": "John Doe",
    "email": "john@example.com",
    "phone": "+1234567890",
    "source": "website-contact-form",
    "status": "new",
    "created_at": "2024-01-15T10:30:00Z",
    "assigned_to": null
  }
}`}
        </CodeBlock>

        <SubTitle>Error Responses</SubTitle>
        <CodeBlock>
          <button className="copy-btn" onClick={() => copyToClipboard('// 401 - Invalid API Key\n{\n  "success": false,\n  "message": "Invalid API key"\n}\n\n// 400 - Validation Error\n{\n  "success": false,\n  "message": "Missing required fields: name and either email or phone are required"\n}\n\n// 400 - Invalid Email\n{\n  "success": false,\n  "message": "Invalid email format"\n}\n\n// 400 - Invalid Phone\n{\n  "success": false,\n  "message": "Invalid phone number format"\n}\n\n// 500 - Server Error\n{\n  "success": false,\n  "message": "Server error while creating lead"\n}')}>
            <FiCopy /> Copy
          </button>
          {`// 401 - Invalid API Key
{
  "success": false,
  "message": "Invalid API key"
}

// 400 - Validation Error
{
  "success": false,
  "message": "Missing required fields: name and either email or phone are required"
}

// 400 - Invalid Email
{
  "success": false,
  "message": "Invalid email format"
}

// 400 - Invalid Phone
{
  "success": false,
  "message": "Invalid phone number format"
}

// 500 - Server Error
{
  "success": false,
  "message": "Server error while creating lead"
}`}
        </CodeBlock>

        <SubTitle>Validation Rules</SubTitle>
        <ul>
          <li><strong>Name:</strong> Required field</li>
          <li><strong>Email:</strong> Must be valid email format (e.g., user@domain.com)</li>
          <li><strong>Phone:</strong> Must be valid international format (e.g., +1234567890)</li>
          <li><strong>Number of Passengers:</strong> Must be a positive number if provided</li>
          <li><strong>Travel Date:</strong> Must be valid date format if provided</li>
          <li><strong>Source:</strong> Optional, defaults to "external_api"</li>
        </ul>
      </Section>
    </ContentCard>
  );

  const metaAdsIntegration = (
    <ContentCard>
      <Section>
        <SectionTitle>
          <FiLink />
          Meta Ads Integration
        </SectionTitle>
        <p>Connect your Facebook and Instagram advertising campaigns to automatically capture lead form submissions and ad interactions using our unified API endpoint.</p>

        <SubTitle>Lead Ads Setup</SubTitle>
        <StepList>
          <Step>
            <strong>Create a Facebook App</strong>
            <p>Go to Facebook Developers and create a new app for your business. Select "Business" as the app type.</p>
          </Step>
          <Step>
            <strong>Add Lead Ads API</strong>
            <p>In your app dashboard, add the "Lead Ads" product and configure the necessary permissions.</p>
          </Step>
          <Step>
            <strong>Configure API Integration</strong>
            <p>Configure Meta Ads to send lead data directly to our API endpoint:</p>
            <CodeBlock>
              <button className="copy-btn" onClick={() => copyToClipboard('API Endpoint: POST https://api.pleasureindiaholidays.com/api/leads\nHeaders: \n  x-api-key: CURDATEYEAR\n  Content-Type: application/json')}>
                <FiCopy /> Copy
              </button>
              {`API Endpoint: POST https://api.pleasureindiaholidays.com/api/lead/leads
Headers: 
  x-api-key: CURDATEYEAR
  Content-Type: application/json`}
            </CodeBlock>
          </Step>
          <Step>
            <strong>Get Access Tokens</strong>
            <p>Generate page access tokens for each Facebook page running lead ads.</p>
          </Step>
        </StepList>

        <SubTitle>API Configuration</SubTitle>
        <ConfigTable>
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Value</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>API Endpoint</code></td>
              <td>POST https://api.pleasureindiaholidays.com/api/lead/leads</td>
              <td>Endpoint to receive lead data</td>
            </tr>
            <tr>
              <td><code>API Key</code></td>
              <td>CURDATEYEAR</td>
              <td>Authentication key for API access</td>
            </tr>
            <tr>
              <td><code>Content-Type</code></td>
              <td>application/json</td>
              <td>Request content type</td>
            </tr>
          </tbody>
        </ConfigTable>

        <SubTitle>Lead Form Configuration</SubTitle>
        <p>When creating lead forms in Meta Ads Manager, use these recommended fields:</p>
        <ul>
          <li><strong>Full Name</strong> - Map to <code>name</code></li>
          <li><strong>Email</strong> - Map to <code>email</code></li>
          <li><strong>Phone Number</strong> - Map to <code>phone</code></li>
          <li><strong>Preferred Destination</strong> - Map to <code>tourName</code></li>
          <li><strong>Travel Date</strong> - Map to <code>travelDate</code></li>
          <li><strong>Number of Travelers</strong> - Map to <code>numberOfPax</code></li>
          <li><strong>Special Requests</strong> - Map to <code>message</code></li>
        </ul>

        <InfoBox>
          <FiInfo className="icon" />
          <div className="content">
            <h4>Data Processing</h4>
            <p>Lead data from Meta will be automatically processed and appear in your CRM within minutes. Each lead will be tagged with "meta-ads" as the source.</p>
          </div>
        </InfoBox>
      </Section>
    </ContentCard>
  );

  const googleAdsIntegration = (
    <ContentCard>
      <Section>
        <SectionTitle>
          <FiSettings />
          Google Ads Integration
        </SectionTitle>
        <p>Integrate with Google Ads to track lead conversions, import lead form extensions data, and optimize your advertising campaigns using our unified API endpoint.</p>

        <SubTitle>Google Ads API Setup</SubTitle>
        <StepList>
          <Step>
            <strong>Enable Google Ads API</strong>
            <p>Go to Google Cloud Console, create a project, and enable the Google Ads API.</p>
          </Step>
          <Step>
            <strong>Create OAuth2 Credentials</strong>
            <p>Set up OAuth2 credentials to access Google Ads data on behalf of your account.</p>
          </Step>
          <Step>
            <strong>Get Developer Token</strong>
            <p>Apply for a Google Ads developer token from your Google Ads account.</p>
          </Step>
          <Step>
            <strong>Configure API Access</strong>
            <p>Add your credentials to the CRM settings:</p>
            <CodeBlock>
              <button className="copy-btn" onClick={() => copyToClipboard('{\n  "client_id": "your-google-ads-client-id.apps.googleusercontent.com",\n  "client_secret": "your-google-ads-client-secret",\n  "developer_token": "your-google-ads-developer-token",\n  "refresh_token": "your-google-oauth-refresh-token",\n  "customer_id": "1234567890"\n}')}>
                <FiCopy /> Copy
              </button>
              {`{
  "client_id": "your-google-ads-client-id.apps.googleusercontent.com",
  "client_secret": "your-google-ads-client-secret",
  "developer_token": "your-google-ads-developer-token",
  "refresh_token": "your-google-oauth-refresh-token",
  "customer_id": "1234567890"
}`}
            </CodeBlock>
          </Step>
        </StepList>

        <SubTitle>Lead Form Extensions</SubTitle>
        <p>Set up lead form extensions in Google Ads to capture leads directly from search ads:</p>
        <ol>
          <li>In Google Ads, go to Ads & Extensions → Extensions</li>
          <li>Create a new Lead Form Extension</li>
          <li>Configure form fields to match your CRM requirements</li>
          <li>Set up automatic data sync with our API</li>
        </ol>

        <SubTitle>Conversion Tracking</SubTitle>
        <p>Track when leads convert to customers by setting up conversion goals:</p>
        <CodeBlock>
          <button className="copy-btn" onClick={() => copyToClipboard('<!-- Google Ads Conversion Tracking -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=AW-XXXXXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag("js", new Date());\n  gtag("config", "AW-XXXXXXXXX");\n</script>')}>
            <FiCopy /> Copy
          </button>
          {`<!-- Google Ads Conversion Tracking -->
<script async src="https://www.googletagmanager.com/gtag/js?id=AW-XXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag("js", new Date());
  gtag("config", "AW-XXXXXXXXX");
</script>`}
        </CodeBlock>

        <SubTitle>Offline Conversion Import</SubTitle>
        <p>Import offline conversions to track the full customer journey:</p>
        <ConfigTable>
          <thead>
            <tr>
              <th>Field</th>
              <th>Google Ads Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>gclid</code></td>
              <td>Google Click ID</td>
              <td>Unique identifier for the ad click</td>
            </tr>
            <tr>
              <td><code>conversion_name</code></td>
              <td>Conversion Action</td>
              <td>Name of your conversion goal</td>
            </tr>
            <tr>
              <td><code>conversion_time</code></td>
              <td>Conversion Date Time</td>
              <td>When the conversion occurred</td>
            </tr>
            <tr>
              <td><code>conversion_value</code></td>
              <td>Conversion Value</td>
              <td>Monetary value of the conversion</td>
            </tr>
          </tbody>
        </ConfigTable>

        <InfoBox>
          <FiInfo className="icon" />
          <div className="content">
            <h4>Campaign Optimization</h4>
            <p>Use the imported lead and conversion data to optimize your Google Ads campaigns for better ROI and lead quality.</p>
          </div>
        </InfoBox>
      </Section>
    </ContentCard>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'website':
        return websiteIntegration;
      case 'meta':
        return metaAdsIntegration;
      case 'google':
        return googleAdsIntegration;
      default:
        return websiteIntegration;
    }
  };

  return (
    <IntegrationContainer>
      <Header>
        <h1>
          <FiLink />
          Lead Source Integration
        </h1>
        <p>
          Connect your lead sources to automatically capture and manage leads in your CRM.
          Choose from website forms, social media advertising, or search engine marketing integrations.
        </p>
      </Header>

      <TabsContainer>
        <Tab
          active={activeTab === 'website'}
          onClick={() => setActiveTab('website')}
        >
          <FiGlobe />
          Website
        </Tab>
        <Tab
          active={activeTab === 'meta'}
          onClick={() => setActiveTab('meta')}
        >
          <FiLink />
          Meta Ads
        </Tab>
        <Tab
          active={activeTab === 'google'}
          onClick={() => setActiveTab('google')}
        >
          <FiSettings />
          Google Ads
        </Tab>
      </TabsContainer>

      {renderContent()}
    </IntegrationContainer>
  );
};

export default IntegrationPage; 